<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div class="modal-card">
      <!-- Close -->
      <button @click="$emit('close')" class="absolute top-3 right-4 text-gray-500 hover:text-gray-700">✕</button>

      <!-- HEADER: Status, Elapsed, Tracking -->
      <div class="grid grid-cols-3 gap-4 mb-6 mt-4">
        <!-- STATUS -->
        <div class="flex justify-between bg-gray-50 dark:bg-gray-800 border rounded-xl p-4 shadow-sm">
          <div class="flex items-center gap-2">
            <img :src="StatusIcon" class="w-5 h-5"/>
            <div>
              <div class="label">Status - {{ formatDateTime(movement?.updated_at) }}</div>
              <div class="value" :class="statusClass">{{ movement?.status }}</div>
            </div>
          </div>
          <div>
            <div class="text-xs text-gray-500 mb-1">Movement ID</div>
            <div class="font-mono text-sm">#{{ movementId }}</div>
          </div>
        </div>

        <!-- ELAPSED -->
        <div class="flex justify-center bg-gray-50 dark:bg-gray-800 border rounded-xl p-4 shadow-sm">
          <div>
            <div class="text-xs text-gray-500 mb-1">Elapsed Time</div>
            <div class="text-lg font-semibold">{{ elapsedTime }}</div>
          </div>
        </div>

        <!-- TRACKING -->
        <div class="flex justify-center bg-gray-50 dark:bg-gray-800 border rounded-xl p-4 shadow-sm">
          <div class="flex flex-col justify-center">
            <div class="label">{{ isSwift ? 'SWIFT Tracking Number (UETR)' : 'E2E Reference' }}</div>
            <div class="font-semibold text-sm break-words">{{ tracking_reference || '-' }}</div>
          </div>
        </div>
      </div>

      <!-- SENDER / RECEIVER -->
      <div class="flex justify-between mb-0">
        <div
            v-for="(stage, index) in stages"
            :key="'top-' + index"
            class="w-full flex justify-center"
        >
          <!-- Sender (only origin) -->
          <div
              v-if="stage.bank_type === 'origin'"
              class="w-56 bg-gray-100 dark:bg-gray-800 border rounded-lg p-3 shadow text-center"
          >
            <div class="text-xs text-gray-500 mb-1">Sender</div>

            <div class="font-semibold text-sm">
              {{ movement.account.user.name }}
            </div>

            <div class="text-xs text-gray-500">
              {{ movement.account.account_number }}
            </div>

            <div class="mt-2 text-xs">
              SWIFT: <span class="font-semibold">{{ movement.account.swift || '-' }}</span>
            </div>
          </div>

          <!-- Receiver (only destination) -->
          <div
              v-if="stage.bank_type === 'destination'"
              class="w-56 bg-gray-100 dark:bg-gray-800 border rounded-lg p-3 shadow text-center"
          >
            <div class="text-xs text-gray-500 mb-1">Receiver</div>

            <div class="font-semibold text-sm">
              {{ movement.beneficiary.name }}
            </div>

            <div class="text-xs text-gray-500">
              {{ movement.beneficiary.country }}, {{ movement.beneficiary.city }}
            </div>

            <div class="mt-2 text-xs">
              SWIFT: <span class="font-semibold">{{ movement.beneficiary.swift }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="timeline relative">
        <div
            v-for="(stage,index) in stages"
            :key="stage.id"
            class="timeline-item flex flex-col items-center relative"
        >

          <!-- Line Before -->
          <div
              v-if="index > 0"
              class="line before absolute left-1/2 -translate-x-1/2"
              :class="lineBeforeClass(stage, stages[index-1])"
          ></div>

          <!-- Circle -->
          <div class="relative z-10 mt-4 flex items-center justify-center w-full">
            <div class="absolute w-10 h-10 rounded-full bg-white"></div>

            <div class="circle" :class="circleClass(stage)">
              <svg
                  v-if="stage.departure_date"
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="white"
              >
                <path
                    d="M20.285 6.708a1 1 0 0 0-1.414-1.416l-9.193 9.193-4.193-4.193a1 1 0 0 0-1.414 1.414l4.9 4.9a1 1 0 0 0 1.414 0l9.91-9.898z"/>
              </svg>
            </div>
          </div>

          <!-- Line After -->
          <div
              v-if="index < stages.length - 1"
              class="line after absolute left-1/2 -translate-x-1/2"
              :class="lineAfterClass(stage)"
          ></div>

          <!-- Stage Card -->
          <div
              class="stage-card w-42 mt-3 text-center bg-gray-50 dark:bg-gray-800 border rounded-lg p-4 shadow flex flex-col items-center relative min-h-[220px]">
            <img :src="BankIcon" class="w-5 h-5 mb-2 mx-auto"/>

            <div class="bank">{{ stage.bank_name?.charAt(0).toUpperCase() + stage.bank_name?.slice(1) }}</div>

            <div class="type">
              {{ stage.bank_type?.charAt(0).toUpperCase() + stage.bank_type?.slice(1) }}
            </div>

            <div v-if="stage.swift_code?.trim()" class="swift">
              SWIFT: {{ stage.swift_code }}
            </div>

            <div class="flex justify-center items-center gap-2">
              <img :src="LocationIcon" class="w-5 h-5"/>
              <span>{{ stage.country || '-' }}</span>
            </div>

            <div class="dates mt-2 flex justify-between gap-4">

              <!-- ORIGIN -->
              <template v-if="stage.bank_type === 'origin'">
                <div class="flex flex-col-reverse items-center justify-center">
                  <div class="text-center">
                    {{ formatDateWithoutTime(stage.departure_date) || '-' }}
                  </div>
                  <div class="flex flex-row items-center">
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 7V3m8 4V3m-9 8h10m-10 4h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 002 2z"/>
                    </svg>
                    <span class="mx-1">→</span>
                  </div>
                </div>
              </template>

              <!-- DESTINATION -->
              <template v-else-if="stage.bank_type === 'destination'">
                <div class="flex flex-col-reverse items-center justify-center">
                  <div class="text-center">
                    {{ formatDateWithoutTime(stage.arrival_date) || '-' }}
                  </div>
                  <div class="flex flex-row items-center">
                    <span class="mx-1">→</span>
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 7V3m8 4V3m-9 8h10m-10 4h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 002 2z"/>
                    </svg>
                  </div>
                </div>


              </template>

              <!-- INTERMEDIATE -->
              <template v-else>
<!--                <div>→ {{ formatDateWithoutTime(stage.arrival_date) || '-' }}</div>-->

                <div class="flex flex-col-reverse items-center justify-center">
                  <div class="text-center">
                    {{ formatDateWithoutTime(stage.arrival_date) || '-' }}
                  </div>
                  <div class="flex flex-row items-center">
                    <span class="mx-1">→</span>
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 7V3m8 4V3m-9 8h10m-10 4h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 002 2z"/>
                    </svg>
                  </div>
                </div>

                <div class="flex flex-col-reverse items-center justify-center">
                  <div class="text-center">
                    {{ formatDateWithoutTime(stage.departure_date) || '-' }}
                  </div>
                  <div class="flex flex-row items-center">
                    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 7V3m8 4V3m-9 8h10m-10 4h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 002 2z"/>
                    </svg>
                    <span class="mx-1">→</span>
                  </div>
                </div>

<!--                <div>{{ formatDateWithoutTime(stage.departure_date) || '-' }} →</div>-->
              </template>

            </div>
          </div>

        </div>
      </div>

    </div>
  </div>
</template>


<script setup>
import {ref, onMounted, computed, watch, defineProps} from 'vue'
import api from '@/services/api'
import {formatDateTime, formatDateWithoutTime} from "@/utils/formatters"

import BankIcon from '@/assets/bank.svg'
import LocationIcon from '@/assets/location.svg'
import StatusIcon from '@/assets/routing.svg'

const props = defineProps({
  show: Boolean,
  movementId: Number,
  movementStatus: String,
  movementType: String
})

const stages = ref([])
const movement = ref(null)
const tracking_reference = ref(null)

const isSwift = computed(() =>
    props.movementType?.toLowerCase() === 'swift_transfer'
)

/* STATUS COLOR */
const statusClass = computed(() => ({
  'text-green-600': ['Approved', 'Completed'].includes(movement.value?.status),
  'text-red-600': movement.value?.status === 'Rejected',
  'text-yellow-600': movement.value?.status === 'Pending',
  'text-blue-600': movement.value?.status === 'Processing'
}))

/* FETCH */
const fetchStages = async () => {
  if (!props.movementId) return;
  const res = await api.get(`/tracking/movements/${props.movementId}/stages`, {
    headers: {Authorization: `Bearer ${localStorage.getItem('token')}`}
  })

  stages.value = res.data.stages || []
  movement.value = res.data.movement
  tracking_reference.value = res.data.tracking_reference
}

/* TIMELINE LOGIC */
const circleClass = (stage) => {
  if (stage.departure_date) return 'done'
  if (stage.arrival_date) return 'active'
  return 'pending'
}

const lineBeforeClass = (stage, prev) => {
  return prev?.departure_date ? 'line-done' : 'line-pending'
}

const lineAfterClass = (stage) => {
  return stage.departure_date ? 'line-done' : 'line-pending'
}

/* ELAPSED */
const elapsedTime = computed(() => {
  if (!stages.value.length || !movement.value?.created_at) return '-'

  const start = new Date(movement.value.created_at)

  const destination = stages.value.find(s => s.bank_type === 'destination')

  const end = destination?.departure_date
      ? parseDateSafe(destination.updated_at)
      : new Date()

  console.log("start", start);
  console.log("end", end);


  const diffMs = end - start

  if (diffMs <= 0) return '0s'

  const totalSeconds = Math.floor(diffMs / 1000)

  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  // 🎯 نمایش هوشمند
  if (days > 0) {
    return `${days}d ${hours}h`
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  }

  return `${seconds}s`
})


const parseDateSafe = (dateStr) => {
  if (!dateStr) return null

  // اگر فقط date هست → فرض کن UTC آخر روز
  if (dateStr.length === 10) {
    return new Date(dateStr + 'T23:59:59Z')
  }

  return new Date(dateStr)
}


onMounted(fetchStages)
watch(() => props.show, fetchStages)
</script>


<style scoped>

.party-card {
  background: #f3f4f6;
  padding: 12px;
  border-radius: 10px;
  width: 45%;
}

/* TIMELINE */
.timeline {
  display: flex;
  justify-content: space-between;
}

.timeline-item {
  width: 100%;
  text-align: center;
  position: relative;
}


.modal-card {
  width: 90vw;
  max-height: 90vh;
  overflow: auto;
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .2);
  position: relative;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 16px;
}

.info-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 12px;
}

.label {
  font-size: 12px;
  color: #888;
}

.value {
  font-weight: 600;
}

.big {
  font-size: 20px;
  font-weight: 700;
}

.mono {
  font-family: monospace;
}

.timeline {
  display: flex;
  justify-content: space-between;
  position: relative;
}

.timeline-item {
  width: 100%;
  text-align: center;
  position: relative;
}

.line {
  position: absolute;
  top: 28px; /* وسط دایره اصلی */
  height: 4px;
  z-index: 0;
}

.before {
  left: 0;
  right: 50%;
}

.after {
  left: 50%;
  right: 0;
}

.line-done {
  background: #10b981;
}

.line-pending {
  background: #e5e7eb;
}

.circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 4px solid gray;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}


.circle.done {
  background: #10b981; /* همون سبز */
  border-color: #10b981; /* همون سبز */
  color: white; /* رنگ تیک سفید */
}

.circle.done span {
  color: white !important;
  font-weight: bold;
  font-size: 14px;
  line-height: 1;
}

.circle.active {
  border-color: #10b981;
  background: white;
  color: #10b981;
}

.circle.pending {
  border-color: #ccc;
  background: white;
  color: #ccc;
}


.stage-card {
  margin-left: 16px;
  margin-right: 16px;
  margin-top: 12px;
  background: #f9fafb;
  padding: 12px;
  border-radius: 12px;

  height: 210px; /* 🔥 مهم */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}


.bank {
  font-weight: 700;
}

.type {
  font-size: 12px;
  color: #666;
}

.swift {
  font-size: 11px;
  color: #aaa;
}

.country {
  margin-top: 6px;
}

.dates {
  margin-top: 10px;
  font-size: 12px;
  display: flex;
  justify-content: space-between;
}

</style>